import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  Signal,
  signal,
  inject,
  viewChild,
  viewChildren
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Layout } from "@auvious/layout";
import { IEndpoint, IPublishedStream, StreamTypes } from "@auvious/rtc";
import { TranslateService } from "@ngx-translate/core";
import { merge } from "rxjs";
import { filter, take } from "rxjs/operators";
import {
  animate,
  state,
  style,
  transition,
  trigger
} from "@angular/animations";
import { IMediaDevicesEvents, MediaDevices } from "@auvious/media-tools";

import {
  AgentMutedAction,
  AppConfigService,
  ApplicationService,
  ArPointerActivatedEvent,
  ArPointerDeactivatedEvent,
  AssistantService,
  CallEndedAction,
  CallHoldChangedAction,
  CallStartedAction,
  CobrowseViewRequestAction,
  ConferenceService,
  ConferenceStore,
  debug,
  debugError,
  DeviceService,
  FEATURES,
  FeedbackRequestedAction,
  FileTransferService,
  GenericErrorHandler,
  LocalMediaService,
  MediaEffectsService,
  MediaRulesService,
  PointerService,
  RatingService,
  SnapshotService,
  StreamState,
  UserService,
  windowActionType,
  WindowEventService
} from "../../services";
import { CobrowseService } from "../../services/cobrowse.service";
import { DeepfakeAutoCheckService } from "../../services/deepfake-auto-check.service";
import { DeepfakeService } from "../../services/deepfake.service";
import { NotificationService } from "../../services/notification.service";
import {
  fadeInOut,
  slideIn,
  slideInOut,
  zoomInOut
} from "../../core-ui.animations";
import {
  CobrowseContextEnum,
  ConferenceMetadataKeyEnum,
  ConversationChannelEnum,
  ConversationDestinationEnum,
  ConversationOriginEnum,
  ConversationTypeEnum,
  EndpointTypeEnum,
  GeolocationError,
  InteractionMetricEnum,
  KEY_COBROWSE_CONTEXT,
  KEY_COBROWSE_SESSION_ID,
  LayoutEnum,
  StreamTrackKindEnum,
  TerminateReasonEnum,
  TileTypeEnum,
  UserCapabilityEnum,
  UserRoleEnum,
  VideoFacingModeEnum
} from "../../core-ui.enums";
import {
  AgentParam,
  CoBrowseMetadata,
  CustomerParam,
  DEFAULT_CALL_HOLD_STATE,
  GeolocationMetadata,
  IApplication,
  ICallHoldState,
  IEndpointMetadata,
  IEndpointMetadataChangedEvent,
  IInteraction,
  INotificationEvent,
  IntegrationMetadata,
  IRecorderInfo,
  IRecorderOptions,
  IStreamMetadata,
  ISurvey,
  ITile,
  PublicParam,
  SpeechToTextMetadata,
  ToastNotification
} from "../../models";
import { ConfirmNotification } from "../../models/notifications/ConfirmNotification";
import { ActivityIndicatorService } from "../../services/activity-indicator.service";
import { TranscriptService } from "../../services/transcript.service";
import { copyTextToClipboard, delay, withTimeout } from "../../services/utils";
import { TileComponent } from "../tile/tile.component";
import type { StreamControlsComponent } from "../stream-controls/stream-controls.component";
import { AnalyticsService } from "../../services/analytics.service";
import { CobrowseRequestType, OriginModeEnum } from "@auvious/integrations";
import { sessionStore } from "@auvious/utils";
import { PictureInPictureStore } from "../../services/store/picture-in-picture.store";
import { IPictureInPictureHost } from "../../core-ui.enums";

@Component({
  selector: "app-room-ui",
  templateUrl: "./room.component.html",
  styleUrls: ["./room.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PictureInPictureStore],
  animations: [
    slideIn,
    slideInOut,
    fadeInOut,
    zoomInOut,
    trigger("toast", [
      transition(":enter", [
        style({ opacity: 0, transform: "translate(-50%, 50px)" }),
        animate(100)
      ]),
      transition(":leave", [
        animate(100, style({ opacity: 0, transform: "translate(-50%, 50px)" }))
      ]),
      state("*", style({ opacity: 1, transform: "translate(-50%, 0)" }))
    ])
  ],
  standalone: false
})
export class RoomComponent
  implements OnInit, OnDestroy, AfterViewInit, IPictureInPictureHost
{
  private zone = inject(NgZone);
  private errorHandler = inject(GenericErrorHandler);
  private deviceService = inject(DeviceService);
  private activityService = inject(ActivityIndicatorService);
  private conferenceService = inject(ConferenceService);
  private translate = inject(TranslateService);
  private notification = inject(NotificationService);
  private cdr = inject(ChangeDetectorRef);
  private config = inject(AppConfigService);
  private windowEventService = inject(WindowEventService);
  private ratingService = inject(RatingService);
  private applicationService = inject(ApplicationService);
  private userService = inject(UserService);
  private cobrowseService = inject(CobrowseService);
  private pointerService = inject(PointerService);
  private mediaEffectsService = inject(MediaEffectsService);
  private snapshotService = inject(SnapshotService);
  private transferService = inject(FileTransferService);
  private mediaRules = inject(MediaRulesService);
  private local = inject(LocalMediaService);
  private asr = inject(TranscriptService);
  store = inject(ConferenceStore);
  private analytics = inject(AnalyticsService);
  private assistant = inject(AssistantService);
  private deepfakeAutoCheck = inject(DeepfakeAutoCheckService);
  private deepfake = inject(DeepfakeService);
  private destroyRef = inject(DestroyRef);
  readonly pipStore = inject(PictureInPictureStore);

  @Input() recorderOptions: IRecorderOptions;
  @Input() interaction: IInteraction;

  @Output() callStarted = new EventEmitter<any>();
  @Output() callPendingRating = new EventEmitter<boolean>();
  @Output() callEnded = new EventEmitter<{
    reason: TerminateReasonEnum;
    error?: Error;
  }>();
  @Output() exit = new EventEmitter();
  @Output() callEnding = new EventEmitter<{
    terminated: boolean;
    terminatedRemotely: boolean;
  }>();
  @Output() deviceSetupComplete = new EventEmitter();
  @Output() layoutChange = new EventEmitter<LayoutEnum>();
  @Output() recorderStarted = new EventEmitter<IRecorderInfo>();
  @Output() recorderStopped = new EventEmitter<IRecorderInfo>();
  @Output() remotePopupClosed = new EventEmitter();

  readonly tileContainerRef =
    viewChild<ElementRef<HTMLDivElement>>("container");
  readonly tilesRef = viewChildren<TileComponent>("tiles");
  readonly audioTest = viewChild<ElementRef>("testSound");
  readonly notificationSoundJoin = viewChild<ElementRef>("notificationIn");
  readonly notificationSoundLeave = viewChild<ElementRef>("notificationOut");
  readonly mediaErrorCardRef = viewChild("mediaErrorCardRef", {
    read: ElementRef
  });
  readonly streamControls =
    viewChild<StreamControlsComponent>("streamControlsRef");
  readonly streamControlsElement = viewChild("streamControlsRef", {
    read: ElementRef<HTMLElement>
  });
  readonly holdElement = viewChild("holdRef", {
    read: ElementRef<HTMLElement>
  });
  readonly streamPlayElement = viewChild("streamPlayRef", {
    read: ElementRef<HTMLElement>
  });
  readonly mediaRequestCardRef = viewChild("mediaRequestCardRef", {
    read: ElementRef<HTMLElement>
  });

  /** true while the call is rendered inside the picture-in-picture window */
  readonly pipActive = computed(() => this.pipStore.isOpen());

  callHoldState: ICallHoldState;
  participantNotificationMap: Map<string, INotificationEvent> = new Map();

  notAllowedError = signal(false);
  streamError = signal(false);
  notFoundError = signal(false);
  isWebViewError = signal(false);
  isMediaRequestTimeoutError = signal(false);
  isRequestingMedia = signal(false);
  agentDisplayName = signal(undefined);
  // callHUDElementColorMode = signal(undefined);

  isAgent = false;
  isSupervisor = false;
  isCustomer = false;
  isGuestAgent = false;
  isDeviceSetup = false;
  isRating = false;
  isCobrowseActive = false;
  isWhisper = false;
  application: IApplication;
  currentLayout: LayoutEnum = LayoutEnum.grid;
  layout: Layout;
  localTile: ITile;
  spotlightTile = signal<ITile | null>(null);
  pinnedTile = signal<ITile | null>(null);
  isDeviceSetupOpen = false;
  isCallEnded = false;
  isLeavingByChoice = false;
  isSnapshotActive = false;
  isSnapshotStarted = false;
  isReviewingSnapshots = false;
  isCaptionsOn = false;
  private secondaryCameraAutoStarted = false;
  conferenceId: string;
  skipRating = false;
  localAudioVideoStream: IPublishedStream;
  endpoints: IEndpoint[] = [];
  spotlightTileMaximise: boolean;
  interactionID: string;
  isInteractionDataVisible: boolean;
  agentLeftTimeout;
  undeliveredSecondaryStreamError;
  openStreamRetrySubscription;

  // map from store
  public streams: StreamState[];
  private storeStreams: StreamState[];
  public snapshotStream: StreamState = null;
  public mystream: StreamState = null;

  private screenStream: StreamState;
  private cobrowseOriginator: IEndpoint; // the agent who requested to co-browse

  url: string;

  constructor() {
    this.application = this.applicationService.getActiveApplication();

    this.callHoldState = { ...DEFAULT_CALL_HOLD_STATE };

    this.url = window.location.href;

    this.zone.runOutsideAngular(() => {
      window.addEventListener("resize", this.onResize.bind(this));
    });
    window.addEventListener("focus", this.onWindowFocus.bind(this));

    // these are inside an @if. If they are re-created while in pip, move them again
    effect(() => {
      const elements = this.getPipElements();
      if (this.pipActive()) {
        elements.forEach((el) => this.pipStore.adopt(el));
      }
    });
  }

  async ngAfterViewInit() {
    this.spotlightTileMaximise = this.config.publicParam(
      PublicParam.LAYOUT_SPOTLIGHT_HIDE_OTHER_PARTICIPANTS
    );

    const tileContainerRef = this.tileContainerRef();
    if (!tileContainerRef?.nativeElement) {
      this.cdr.detectChanges();
    }

    if (!tileContainerRef?.nativeElement) {
      await new Promise(requestAnimationFrame);
    }

    this.pipStore.connect(this);

    this.layout = new Layout({
      margin: this.isScreenShareFullScreen ? 5 : 10,
      height: tileContainerRef.nativeElement.clientHeight,
      width: tileContainerRef.nativeElement.clientWidth,
      focus: { maximize: this.spotlightTileMaximise }
    });

    try {
      await Promise.all([
        MediaDevices.syncSpeaker(this.audioTest().nativeElement),
        MediaDevices.syncSpeaker(this.notificationSoundJoin().nativeElement),
        MediaDevices.syncSpeaker(this.notificationSoundLeave().nativeElement)
      ]);
    } catch (ex) {
      this.errorHandler.handleError(ex);
    }
  }

  async ngOnInit() {
    this.mediaEffectsService.init(this.interaction);

    // the automatic media-authenticity check watches streams for the whole
    // call; it is a no-op unless the application turned it on for agents
    this.deepfakeAutoCheck.attach({
      interactionId: this.interaction?.getId(),
      conferenceId: () => this.conferenceService.session?.conference?.name
    });

    this.store.updated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (
          this.storeStreams !== this.store.streams ||
          this.screenStream !== this.store.screenStream ||
          this.snapshotStream !== this.store.snapshotStream
        ) {
          this.storeStreams = this.store.streams;
          // the screen a customer shares for co-browse is shown only in app-cobrowse-session
          this.streams = this.store.streams.filter((s) => !s.cobrowse);
          this.screenStream = this.store.screenStream;
          this.snapshotStream = this.store.snapshotStream;

          if (this.snapshotStream) {
            this.isSnapshotActive = true;
          }

          if (this.store.mystream) {
            this.mystream = this.store.mystream;
            this.resetStreamErrors();
            if (
              this.conferenceService.getParticipants()[
                this.conferenceService.myself.endpoint
              ]?.metadata?.mediaDevicesError
            ) {
              this.conferenceService.updateEndpointMetadata({
                mediaDevicesError: false,
                mediaDevices: MediaDevices.getDeviceList()
              });
            }
            this.isDeviceSetupOpen = !this.deviceService.isDevicesSetup;
          }

          this.cdr.detectChanges();
        }
      });

    this.isAgent = this.userService.isAgent;
    this.isSupervisor = this.userService.isSupervisor;
    this.isGuestAgent = this.userService.isGuestAgent;
    this.isCustomer = this.userService.isCustomer;
    this.conferenceId = this.interaction.getRoom();
    this.isLeavingByChoice = false;
    this.secondaryCameraAutoStarted = false;
    this.interactionID = this.interaction.getId();
    this.isWhisper = !!this.interaction.getWhisperMode();
    this.isInteractionDataVisible =
      this.config.publicParam(PublicParam.INTERACTION_DATA_VISIBLE) ??
      this.config.customerParamEnabled(CustomerParam.INTERACTION_DATA_VISIBLE);

    this.local.settingUp.then(async () => {
      const permissionState: {
        audio: PermissionStatus;
        video: PermissionStatus;
      } = {
        audio: undefined,
        video: undefined
      };
      if (navigator.permissions) {
        try {
          // https://developer.mozilla.org/en-US/docs/Web/API/Permissions/query
          const p = await Promise.all([
            navigator.permissions.query({ name: "microphone" }),
            navigator.permissions.query({ name: "camera" })
          ]);
          permissionState.audio = p[0];
          permissionState.video = p[1];
        } catch (ex) {
          debugError("Permission query failed", ex);
        }
      }

      const requestAudioPermission =
        !this.isWhisper &&
        // audio on by default
        this.mediaRules.joinWithAudioOn &&
        // audio not disabled
        this.mediaRules.isAudioAvailable &&
        // permissions not granted
        !MediaDevices.permissions.audioinput &&
        // we have not accepted or denied
        (!permissionState.audio || permissionState.audio.state === "prompt");

      const requestVideoPermisson =
        !this.isWhisper &&
        // cam on by default
        this.mediaRules.joinWithVideoOn &&
        // cam not disabled
        this.mediaRules.isVideoAvailable &&
        // permissions not granted
        !MediaDevices.permissions.videoinput &&
        // we have not accepted or denied
        (!permissionState.video || permissionState.video.state === "prompt");

      this.isRequestingMedia.set(
        requestAudioPermission || requestVideoPermisson
      );

      this.cdr.detectChanges();

      this.activityService.loading(false);
    });

    this.initRoom();

    if (this.isAgent) {
      this.snapshotService.load(this.interaction);

      this.conferenceService.endpointMetadataChanged$
        .pipe(
          filter((e) => !!e),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe((m) => {
          this.participantMediaErrorChange(
            m,
            (m.newMetadata as IEndpointMetadata).mediaDevicesError
          );
        });
    }
    if (this.isCustomer) {
      this.conferenceService.conferenceMetadataSet$
        .pipe(
          filter((m) => m instanceof IntegrationMetadata),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe((m: IntegrationMetadata) => {
          // update interaction id with the integration one
          this.interactionID = m.interactionId;
        });
      this.conferenceService.streamAdded$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((stream) => {
          this.clearAgentLeftTimeout(stream.originator);
        });
    }

    this.conferenceService.terminateConference$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((reason: TerminateReasonEnum) => {
        this.leaveConference(reason);
      });
    this.conferenceService.confirmTerminateConference$
      .pipe(
        filter(
          (_) => // do not show for email interactions as requested by integrator
            this.interaction.getChannel() !==
            ConversationChannelEnum.genesysEmail
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((_) => {
        const cnotif = new ConfirmNotification(
          "Conversation ended",
          "Would you like to end the call?"
        );
        cnotif.onConfirmed(() => {
          if (!this.isReviewingSnapshots && !this.isRating) {
            this.leaveConference(TerminateReasonEnum.conversationEnded);
          }
        });
        cnotif.onCanceled(() => {
          // nothing
        });
        this.notification.notify(cnotif);
      });

    this.conferenceService.localStreamPublished$
      .pipe(
        filter((s) => !!s && s.type !== StreamTypes.SCREEN),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((stream) => {
        this.localAudioVideoStream = stream;
        // swisscom
        if (
          this.canAutoStartSecondaryCamera &&
          !stream.isMuted("video") &&
          (stream.getMetadata() as IStreamMetadata).primary
          // todo: also check for mobile. currently secondary cam doesn't open in iphone and then creates a switch-cam issue
        ) {
          this.tryOpenSecondaryCamera();
        }
        this.cdr.detectChanges();
      });

    this.local.streamMutedChange$
      .pipe(
        filter((e) => e.trackKind === StreamTrackKindEnum.video && !e.muted),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (this.canAutoStartSecondaryCamera) {
          this.tryOpenSecondaryCamera();
        }
      });

    this.conferenceService.endpointLeft$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((participant) => {
        this.participantMediaErrorChange(
          {
            newMetadata: participant.metadata,
            userId: participant.username,
            userEndpointId: participant.endpoint
          },
          false
        );
        this.endpoints = this.endpoints.filter(
          (e) => e.endpoint !== participant.endpoint
        );

        // notify guest agents / supervisors that co-browse ended
        if (
          this.isAgent &&
          (participant?.metadata as IEndpointMetadata)?.type ===
            EndpointTypeEnum.coBrowse
        ) {
          const cobrowseOriginator = (participant.metadata as IEndpointMetadata)
            ?.cobrowseOriginator;

          if (
            (!!cobrowseOriginator &&
              cobrowseOriginator.username !==
                this.conferenceService.myself.username) ||
            // this is the old check, in case a previous version of widget is used
            (!cobrowseOriginator && this.isGuestAgent)
          ) {
            this.notification.info("Co-browse", {
              body: "The co-browse session has ended."
            });
          }
        }
        this.cdr.detectChanges();
      });
    this.conferenceService.endpointJoined$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((participant) => {
        this.endpoints = [...this.endpoints, participant];

        this.setAgentName(participant);

        this.clearAgentLeftTimeout(participant);

        // do not show message if customer
        if (!this.isCustomer && participant.metadata?.mediaDevicesError) {
          this.participantMediaErrorChange(
            {
              newMetadata: participant.metadata,
              userEndpointId: participant.endpoint,
              userId: participant.username
            },
            true
          );
        }
        if (
          this.isAgent &&
          (participant?.metadata as IEndpointMetadata)?.type ===
            EndpointTypeEnum.coBrowse
        ) {
          const originator = (participant.metadata as IEndpointMetadata)
            ?.cobrowseOriginator;
          if (originator) {
            // on customer page refresh, we got a null originator
            this.cobrowseOriginator = originator;
          }
          // This check is to proof it for mutliple agents. only the one who requested the session
          // should continue to request permissions
          if (
            (!!this.cobrowseOriginator &&
              this.cobrowseOriginator.username !==
                this.conferenceService.myself.username) ||
            // this is the old check, in case a previous version of widget is used
            (!this.cobrowseOriginator && this.isGuestAgent)
          ) {
            this.notification.info("Co-browse", {
              body: "The agent has requested to co-browse with the customer."
            });
          } else {
            if (this.cobrowseService.isRemoteParticipantConnected) {
              this.cobrowseService.participantRejoined(participant);
            }
            // we have a new cobrowse request, ask to view
            this.cobrowseService.setRemoteCobrowseParticipant(participant);

            const existingSession = sessionStore.getItem(
              KEY_COBROWSE_SESSION_ID
            );
            const existingSessionContext =
              sessionStore.getItem(KEY_COBROWSE_CONTEXT);
            if (
              existingSession &&
              existingSessionContext === CobrowseContextEnum.CONFERENCE &&
              this.cobrowseService.isSavedSessionOfConference(this.conferenceId)
            ) {
              this.isCobrowseActive = true;
            } else {
              this.analytics.trackCobrowseRequested(
                this.interaction,
                participant
              );
            }
          }
        }
        this.cdr.markForCheck();
      });

    this.cobrowseService.ended$
      // I am the one who started the co-browse, I should clear it
      .pipe(
        filter(
          (e) =>
            this.cobrowseOriginator?.endpoint ===
            this.conferenceService.myself.endpoint
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((_) => {
        this.conferenceService.removeConferenceMetadata(
          ConferenceMetadataKeyEnum.coBrowse
        );
        this.cobrowseOriginator = null;
        this.cobrowseService.setStreamTarget(null);
      });

    this.cobrowseService.participantInvited$
      .pipe(
        filter((p) => !!p),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((p) => {
        // a native customer co-browses from the call endpoint, no co-browse endpoint will join to tell who requested it
        if (this.cobrowseService.isNativeTarget) {
          this.cobrowseOriginator = this.conferenceService.myself;
        }
        this.isCobrowseActive = true;
        this.cdr.markForCheck();
      });

    // this.subscriptions.add(
    //   this.conferenceService.screenShareStream$.subscribe((stream) => {
    //     this.screenStream = stream;
    //     if (!this.isScreenShareLocal && this.isChildWindow) {
    //       // in case of remote screen share, disable local on widget and show card on audio call
    //       this.windowEventService.sendMessage(
    //         new ScreenShareAvailabilityChangedAction(!!this.screenStream)
    //       );
    //     }
    //     this.cdr.markForCheck();
    //   })
    // );

    this.conferenceService.callHoldStateChange$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((st) => {
        this.callHoldState = st;
        if (this.isChildWindow) {
          this.windowEventService.sendMessage(
            new CallHoldChangedAction(this.callHoldState.isOnHold)
          );
        }
        this.cdr.detectChanges();
        if (!this.isOnHold) {
          this.onResize(false);
        }
      });

    this.conferenceService.participantLeft$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((participant) => {
        try {
          if (this.isCustomer && participant)
            this.informCustomerAgentLeft(participant);

          if (participant?.metadata?.origin === ConversationOriginEnum.POPUP) {
            // we assume that since an endpoint of type popup left, then the popup was closed
            this.remotePopupClosed.emit();
          }
        } catch (ex) {
          debugError(ex);
        }
      });

    this.pointerService.pointAreaChange$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async (data) => {
        debug("point-area:point area change:", data);
        switch (data.type) {
          case ArPointerActivatedEvent.type: {
            const prevArPointer = this.spotlightTile();
            if (prevArPointer) {
              this.layout?.tile(prevArPointer.container, prevArPointer.ratio);
            }
            this.pinnedTile.set(null);
            this.spotlightTile.set(
              this.tilesRef().find(
                (t) =>
                  t.endpoint === data.targetUserEndpointId &&
                  t.stream.correlationId === data.targetStreamCorrelationId
                // t.stream?.type === data.targetStreamType
              )
            );
            this.decideLayout();
            break;
          }
          case ArPointerDeactivatedEvent.type:
            if (!this.spotlightTile() || data.targetStreamType === "screen") {
              return;
            }
            this.layout.tile(
              this.spotlightTile().container,
              this.spotlightTile().ratio
            );
            this.spotlightTile.set(this.pinnedTile() ?? null);
            this.decideLayout();
            break;
        }
        this.cdr.markForCheck();
      });

    this.snapshotService.started$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        this.isSnapshotStarted = true;
        const prevSnapshot = this.spotlightTile();
        const newSnapshotTile = this.tilesRef().find(
          (t) => t.stream.correlationId === data.targetStreamCorrelationId
        );
        if (
          newSnapshotTile &&
          prevSnapshot &&
          prevSnapshot !== newSnapshotTile
        ) {
          this.layout?.tile(prevSnapshot.container, prevSnapshot.ratio);
        }
        this.pinnedTile.set(null);
        this.spotlightTile.set(newSnapshotTile);
        if (this.spotlightTile()) {
          this.captureGeolocation();
          this.decideLayout();
        } else {
          this.isSnapshotStarted = false;
        }
      });

    this.snapshotService.ended$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((e) => {
        this.deviceService.clearGeolocationTimer();
        if (this.isReviewingSnapshots) {
          return;
        }
        this.isSnapshotActive = false;
        this.isSnapshotStarted = false;
        if (this.spotlightTile()) {
          this.layout.tile(
            this.spotlightTile().container,
            this.spotlightTile().ratio
          );
          this.spotlightTile.set(this.pinnedTile() ?? null);
          this.decideLayout();
        }
      });

    this.snapshotService.reviewing$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((e) => {
        this.isReviewingSnapshots = true;
        this.isSnapshotActive = true;
        this.cdr.markForCheck();
      });

    merge(
      this.conferenceService.conferenceMetadataSet$.pipe(
        filter((m) => m instanceof SpeechToTextMetadata)
      ),
      this.asr.enabled$
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((m: SpeechToTextMetadata) => {
        this.isCaptionsOn = true;
        this.cdr.detectChanges();
        this.onResize(true);
      });
    merge(
      this.conferenceService.conferenceMetadataRemoved$.pipe(
        filter((m) => m instanceof SpeechToTextMetadata)
      ),
      this.asr.disabled$
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((m) => {
        this.isCaptionsOn = false;
        this.cdr.detectChanges();
        this.onResize(true);
      });

    this.conferenceService.conferenceMetadataSet$
      .pipe(
        filter((m) => m instanceof CoBrowseMetadata),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((m: CoBrowseMetadata) => {
        this.cobrowseService.setStreamTarget(m.target);
      });

    this.conferenceService.conferenceMetadataRemoved$
      .pipe(
        filter((m) => m instanceof CoBrowseMetadata),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((m: CoBrowseMetadata) => {
        this.cobrowseService.setStreamTarget(null);
      });

    this.local.changedDevices$
      .pipe(
        filter((_) => this.conferenceService.sessionExists),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((p) => {
        this.conferenceService.updateEndpointMetadata({
          mediaDevices: MediaDevices.getDeviceList()
        });
      });

    if (this.isAgent) {
      this.conferenceService.conferenceMetadataSet$
        .pipe(
          filter((m) => m instanceof GeolocationMetadata),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe((m: GeolocationMetadata) => {
          if (!m.error) {
            this.analytics.updateInteractionMetrics(this.interaction, {
              [InteractionMetricEnum.snapshotGeolocation]: m.coordinates
            });
            this.notification.success("Customer location shared", {
              body: "The location has been saved"
            });
          } else {
            let body;
            switch (m.error) {
              case GeolocationError.PERMISSION_DENIED:
                body = "Customer denied to share the location";
                break;
              case GeolocationError.POSITION_UNAVAILABLE:
                body = "The location is not available in the customer's device";
                break;
              case GeolocationError.TIMEOUT:
                body = "The device timed out trying to capture the location";
                break;
            }
            this.notification.info("Customer location unavailable", { body });
          }
        });
    }

    if (this.isChildWindow) {
      this.windowEventService
        .init(this.interaction.getParentFrameUrl())
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((action) => {
          if (!action) {
            return;
          }
          switch (action.type) {
            case windowActionType.LEAVE_CALL:
              // the payload carries if it's a transfer or not.
              // we should skip rating if it's a transfer.
              this.skipRating = action.payload === TerminateReasonEnum.transfer;
              this.leaveConference(action.payload);
              break;
          }
        });
      if (this.isAudioCall) {
        this.conferenceService.streamMutedChange$
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((data) => {
            if (
              data.stream.originator.endpoint !== this.endpoint &&
              this.isCustomer &&
              data.trackKind === StreamTrackKindEnum.audio
            ) {
              this.windowEventService.sendMessage(
                new AgentMutedAction(data.muted)
              );
            }
          });
        this.conferenceService.streamAdded$
          .pipe(
            filter(
              (s) =>
                s.originator.endpoint !== this.endpoint &&
                this.isCustomer &&
                s.type !== "screen"
            ),
            takeUntilDestroyed(this.destroyRef)
          )
          .subscribe((stream) => {
            this.windowEventService.sendMessage(
              new AgentMutedAction(stream.isMuted("audio"))
            );
          });
      }
    }
  }

  ngOnDestroy() {
    debug("destroying conference page");
    // move the call UI back before tearing down, e.g. when hanging up from the pip window
    this.pipStore.close();
    // leaving room by clicking the back button or some other type of browser navigation
    if (!this.isLeavingByChoice) {
      this.leaveConference();
    }
    if (this.agentLeftTimeout) {
      clearTimeout(this.agentLeftTimeout);
    }
    if (this.assistant.hasActiveSession()) {
      this.assistant.destroyLiveSession();
    }
    this.deviceService.isDevicesSetup = false;
    this.windowEventService.destroy();
    this.mediaEffectsService.disable();
    this.snapshotService.destroy();
    this.deepfakeAutoCheck.detach();
    this.deepfake.reset();
    this.pointerService.cleanup();
    this.conferenceService.destroy();
    this.local.reset();

    MediaDevices.desyncSpeaker(this.audioTest().nativeElement);
    MediaDevices.desyncSpeaker(this.notificationSoundJoin().nativeElement);
    MediaDevices.desyncSpeaker(this.notificationSoundLeave().nativeElement);

    this.zone.runOutsideAngular(() => {
      window.removeEventListener("resize", this.onResize.bind(this));
      window.removeEventListener("focus", this.onWindowFocus.bind(this));
    });
  }

  @HostListener("window:beforeunload", ["$event"])
  beforeunload($event) {
    if (
      // the call has already ended/left, nothing to warn about. Also lets a
      // popped out call window close itself without a confirmation dialog.
      this.isLeavingByChoice ||
      this.interaction.getDestination() ===
        ConversationDestinationEnum.PREMISE ||
      (this.isAgent &&
        !!this.config.agentParam(AgentParam.CALL_ENDED_REDIRECT_URL))
    ) {
      return;
    }
    return ($event.returnValue = true);
  }

  @HostListener("window:unload", ["$event"])
  unload($event) {
    if (!this.isWidget) {
      // if (this.isAgent && this.canTerminate) {
      //   this.terminateConference();
      // } else {
      this.leaveConference(TerminateReasonEnum.refresh);
      // }
    }
  }

  private resize(animateMe: boolean) {
    const tileContainerRef = this.tileContainerRef();
    if (this.layout && tileContainerRef?.nativeElement) {
      this.layout.update({
        width: tileContainerRef.nativeElement.clientWidth,
        height: tileContainerRef.nativeElement.clientHeight
      });

      this.layout.render(animateMe === true);
    }
  }

  onResize(animateMe = false) {
    if (this.isMobile) {
      setTimeout(() => this.resize(animateMe), 300);
    } else {
      this.resize(animateMe);
    }
  }

  onWindowFocus() {
    setTimeout(() => {
      this.conferenceService.playPausedStreams();
    }, 200);
  }

  /* picture-in-picture host */

  getPipElements(): HTMLElement[] {
    return [
      this.tileContainerRef()?.nativeElement,
      this.streamControlsElement()?.nativeElement,
      this.holdElement()?.nativeElement,
      // the pip document needs its own user gesture to play remote audio
      this.streamPlayElement()?.nativeElement,
      // permission and device error help
      this.mediaRequestCardRef()?.nativeElement,
      this.mediaErrorCardRef()?.nativeElement,
      // toasts and confirmations that need an answer during the call (location, desktop capture,
      // AR pointer...). It lives in the app shell, outside this component
      document.querySelector<HTMLElement>("app-notification-manager")
    ].filter((el) => !!el);
  }

  pipOpened(pipWindow: Window) {
    this.zone.runOutsideAngular(() => {
      // the pip window is discarded when closed, no need to remove the listener
      pipWindow.addEventListener("resize", () => this.onResize());
    });
    this.relayoutForPip();
  }

  pipClosed() {
    this.relayoutForPip();
  }

  private relayoutForPip() {
    // apply the container css class before the layout measures the container
    this.cdr.detectChanges();
    this.decideLayout();
  }


  /**
   * co-browse and snapshot tiles are hidden in pip and must not take a slot in the grid.
   * A tile can end up there when it re-emits ready (tileReady puts the previous spotlight
   * back in the grid).
   */
  private untileHiddenInPip() {
    const spotlight = this.spotlightTile();
    if (spotlight && !this.layoutSpotlightTile) {
      this.layout.untile(spotlight.container);
    }
  }

  /**
   * the tile to spotlight. In pip, co-browse and snapshot are hidden, so they are not
   * spotlighted.
   */
  private get layoutSpotlightTile(): ITile | null {
    const tile = this.spotlightTile();
    if (!tile || !this.pipActive()) {
      return tile;
    }
    switch (tile.type) {
      case TileTypeEnum.coBrowse:
      case TileTypeEnum.snapshot:
        return null;
      default:
        return tile;
    }
  }

  async tileReady(tile: ITile) {
    const tileContainerRef = this.tileContainerRef();
    if (!tileContainerRef?.nativeElement || !this.layout) {
      this.cdr.detectChanges();
    }

    if (!tileContainerRef?.nativeElement || !this.layout) {
      await new Promise(requestAnimationFrame);
    }

    switch (tile.type) {
      case TileTypeEnum.video:
        if (!this.isScreenShareFullScreen) {
          debug("layout:tile", tile?.uuid);
          if ((tile as TileComponent).isLocal) {
            debug("layout:tile-ready-local", tile.uuid);
            this.localTile = tile;
          }
          this.layout?.tile(tile.container, tile.ratio || 1);
        }
        break;
      case TileTypeEnum.screenShare:
        if (this.isScreenShareFullScreen) {
          debug("layout:tile", tile.uuid);
          this.layout?.tile(tile.container, tile.ratio);
        } else {
          debug("layout:tile-ready-spotlight", tile.uuid);
          const prevScreenShare = this.spotlightTile();
          if (prevScreenShare) {
            this.layout?.tile(prevScreenShare.container, prevScreenShare.ratio);
          }
          this.pinnedTile.set(null);
          this.spotlightTile.set(tile);
        }
        break;
      case TileTypeEnum.coBrowse:
      case TileTypeEnum.arPointer:
      case TileTypeEnum.snapshot:
        debug("layout:tile-ready-spotlight", tile.uuid);
        if (this.isReviewingSnapshots) {
          this.localTile = tile;
          this.layout?.tile(tile.container, tile.ratio);
        } else {
          const prevCoBrowse = this.spotlightTile();
          if (prevCoBrowse) {
            this.layout?.tile(prevCoBrowse.container, prevCoBrowse.ratio);
          }
          this.pinnedTile.set(null);
          this.spotlightTile.set(tile);
        }
        break;
    }
    this.decideLayout();
    this.conferenceService.checkQueueForPendingMetadata();
  }

  async tileDestroyed(tile: ITile) {
    // this.zone.runOutsideAngular(async _ => {
    debug("layout:untile", tile?.uuid);
    // Spotlighted tiles live in the focuser, not the main grid — untile() is a
    // no-op for them. unfocus() properly clears the focuser so the next focus()
    // call starts with a clean slate.
    if (tile.uuid === this.spotlightTile()?.uuid) {
      this.layout?.unfocus();
    } else {
      this.layout?.untile(tile.container);
    }
    switch (tile.type) {
      case TileTypeEnum.video:
        if ((tile as TileComponent).isLocal) {
          this.localTile = null;
        }
        if (tile.uuid === this.pinnedTile()?.uuid) {
          this.pinnedTile.set(null);
          this.spotlightTile.set(null);
        }
        break;
      case TileTypeEnum.screenShare:
      case TileTypeEnum.arPointer:
        this.spotlightTile.set(this.pinnedTile() ?? null);
        break;
      case TileTypeEnum.coBrowse:
        this.spotlightTile.set(this.pinnedTile() ?? null);
        this.isCobrowseActive = false;
        break;
    }
    this.decideLayout();
    // });
  }

  private trySpotlight(): boolean {
    // in case we are in spotlight and everybody leaves, center the tile
    if (
      this.spotlightTile()?.type === TileTypeEnum.snapshot &&
      this.tilesRef().length === 0 // used to be 1 (why?)
    ) {
      this.localTile = this.spotlightTile();
      this.layout.tile(this.localTile.container, this.localTile.ratio);
      this.spotlightTile.set(null);
      return false;
    }

    // if there is no need to go into spotlight, leave
    const spotlightTile = this.layoutSpotlightTile;
    if (!spotlightTile || this.isScreenShareFullScreen) {
      return false;
    }

    switch (this.currentLayout) {
      case LayoutEnum.floating:
        debug("layout:unfloat");
        this.layout.unfloat();
        // i am the receiver of spotlight, unfloat me
        if (spotlightTile.uuid === this.localTile?.uuid) {
        } else {
          // someone else is the receiver, add me back to grid
          debug("layout:tile", this.localTile.uuid);
          this.layout.tile(this.localTile.container, this.localTile.ratio);
          this.localTile.setLayoutType(LayoutEnum.grid);
          // if ([TileTypeEnum.arPointer, TileTypeEnum.video].includes(spotlightTile.type)) {
          //     debug('layout:untile', spotlightTile.uuid);
          //     this.layout.untile(spotlightTile.container);
          // }
        }
        break;
      case LayoutEnum.spotlight:
        // we are already in layout
        // maybe we need to resize, so re-set
        this.layout.focus(
          spotlightTile.container,
          spotlightTile.ratio
        );
        spotlightTile.isSpotlight = true;
        spotlightTile.setLayoutType(LayoutEnum.spotlight);
        return true;
      case LayoutEnum.grid:
        // if ([TileTypeEnum.arPointer, TileTypeEnum.video].includes(spotlightTile.type)) {
        //     debug('layout:untile', spotlightTile.uuid);
        //     this.layout.untile(spotlightTile.container);
        // }
        break;
    }
    debug("layout:focus", spotlightTile.uuid);
    this.layout.focus(
      spotlightTile.container,
      spotlightTile.ratio
    );
    this.currentLayout = LayoutEnum.spotlight;
    spotlightTile.isSpotlight = true;
    spotlightTile.setLayoutType(LayoutEnum.spotlight);
    return true;
  }

  private tryFloat(): boolean {
    // if there is no need to go into float, leave
    // (this.streamsArray.length > 3 || this.streamsArray.length == 1)
    if (
      this.streams.length !== 2 ||
      !this.localTile ||
      !!this.layoutSpotlightTile
    ) {
      return false;
    }
    try {
      switch (this.currentLayout) {
        // case LayoutEnum.floating:
        // we are already in layout
        // return true;
        case LayoutEnum.spotlight:
          // go from spotlight to floating
          // if ([TileTypeEnum.screenShare, TileTypeEnum.coBrowse].includes(this.spotlightTile.type)) {
          try {
            debug("layout:unfocus");
            this.layout.unfocus();
          } catch (ex) {
            debugError(ex);
          }
          // }
          break;
      }
      // debug('layout:untile', this.localTile.uuid);
      // this.layout.untile(this.localTile.container);
      debug("layout:float", this.localTile.uuid);
      const containerHeight =
        this.tileContainerRef().nativeElement.clientHeight;
      const sHeight = containerHeight / 5;

      const floatOptions = this.pipActive()
        ? {
            ratio: this.localTile.ratio,
            height: Math.max(containerHeight / 4, 60),
            top: 8,
            right: 8
          }
        : {
            ratio: this.localTile.ratio,
            height:
              sHeight < 130
                ? this.localTile.isOrientationPortrait
                  ? 180
                  : 130
                : sHeight,
            top: this.isTopBarVisible ? -30 : 10,
            right: 10
          };

      if (this.layout.floating.contains(this.localTile.container)) {
        // float() ignores new options while the tile is already floating, e.g. when the
        // call moves in or out of the pip window, where the container has no top offset
        this.layout.floating.update({}, floatOptions);
      } else {
        this.layout.float(this.localTile.container, floatOptions);
      }
      this.currentLayout = LayoutEnum.floating;
      this.localTile.setLayoutType(LayoutEnum.floating);
      return true;
    } catch (ex) {
      debugError(ex);
      return false;
    }
  }

  private tryGrid(): boolean {
    switch (this.currentLayout) {
      case LayoutEnum.floating:
        debug("layout:unfloat");
        this.layout.unfloat();
        if (this.localTile) {
          this.layout.tile(this.localTile.container, this.localTile.ratio);
          this.localTile.setLayoutType(LayoutEnum.grid);
        }
        break;
      case LayoutEnum.grid:
        return true;
      case LayoutEnum.spotlight:
        try {
          debug("layout:unfocus");
          this.layout.unfocus();
        } catch (ex) {
          debugError(ex);
        }
        break;
    }
    this.currentLayout = LayoutEnum.grid;
    return true;
  }

  // private syncCallHUD() {
  //   setTimeout(() => {
  //   if (this.callHUDRef && this.isWhisper) {
  //     const style = getComputedStyle(this.callHUDRef.nativeElement);
  //     const bkgColor = new TinyColor(style.backgroundColor);
  //     // if we have a light callHUD, set the elements icon color to dark;
  //     this.callHUDElementColorMode.set(bkgColor.isLight() ? "dark" : "light");
  //   } else {
  //     this.callHUDElementColorMode.set("light");
  //   }
  //   }, 100);
  // }

  private async decideLayout() {
    try {
      this.tilesRef().forEach((t) => (t.isSpotlight = false));

      this.tryFloat() || this.trySpotlight() || this.tryGrid();

      if (this.pipActive()) {
        this.untileHiddenInPip();
      }

      this.store.layoutChanged(this.currentLayout);

      const tileContainerRef = this.tileContainerRef();
      if (!tileContainerRef?.nativeElement) {
        this.cdr.detectChanges();
      }

      this.layout?.update({
        width: tileContainerRef.nativeElement.clientWidth,
        height: tileContainerRef.nativeElement.clientHeight
      });

      await this.layout?.render(true);

      debug("layout:----------------------");
      this.layoutChange.emit(this.currentLayout);
    } catch (ex) {
      debugError(ex);
    } finally {
      this.cdr.markForCheck();
    }
  }

  private setAgentName(endpoint: IEndpoint) {
    if (
      !endpoint?.metadata?.roles?.includes(UserRoleEnum.agent) ||
      // do not set the name if a monitoring supervisor joins
      ((endpoint?.metadata as IEndpointMetadata)?.whisperMode &&
        endpoint?.metadata?.roles.includes(UserRoleEnum.supervisor))
    ) {
      return;
    }
    if (
      !!(
        this.config.publicParam(PublicParam.AGENT_NAME_MASK) as string
      )?.trim() &&
      this.interaction.getType() !== ConversationTypeEnum.voiceCall
    ) {
      this.agentDisplayName.set(
        this.config.publicParam(PublicParam.AGENT_NAME_MASK).trim()
      );
    } else {
      this.agentDisplayName.set(endpoint?.metadata?.name?.trim());
    }
  }

  private informCustomerAgentLeft(participant: IEndpoint<IEndpointMetadata>) {
    // check if we should inform customer that an agent left
    const getActiveAgents = () => {
      const participants = this.conferenceService.getParticipants();
      return Object.keys(participants)
        .filter(
          (key) =>
            participants[key].metadata?.roles?.includes(UserRoleEnum.agent) &&
            // in case the stream was removed but we have not received 'endpointLeft' yet
            key !== participant.endpoint
        )
        .map((key) => participants[key]);
    };

    const sec = Number(
      this.config.customerParam(CustomerParam.AGENT_LEFT_TIMEOUT)
    );
    if (
      participant.metadata?.roles?.includes(UserRoleEnum.agent) &&
      sec &&
      getActiveAgents().length === 0 &&
      !this.isOnHold // during transfer we are on hold
    ) {
      this.agentLeftTimeout = setTimeout(() => {
        // check again if we have agents in the call
        if (getActiveAgents().length === 0) {
          this.notification.info("Agent appears to be unavailable", {
            body: "Please try contacting us again.",
            ttl: -1
          });
        }
      }, sec * 1000);
    }
  }

  private clearAgentLeftTimeout(participant: IEndpoint<IEndpointMetadata>) {
    if (
      this.agentLeftTimeout &&
      this.isCustomer &&
      participant.metadata.roles.includes(UserRoleEnum.agent)
    ) {
      clearTimeout(this.agentLeftTimeout);
      this.agentLeftTimeout = undefined;
    }
  }

  private participantMediaErrorChange(
    event: IEndpointMetadataChangedEvent,
    mediaDevicesError: boolean
  ) {
    if (mediaDevicesError) {
      // do not show a second notification for the same endpoint
      if (!this.participantNotificationMap.has(event.userEndpointId)) {
        // prepare a notification for the specific endpoint
        const notif = new ToastNotification(
          !!(event.newMetadata as IEndpointMetadata).name
            ? (event.newMetadata as IEndpointMetadata).name +
              " " +
              this.translate.instant("is facing connectivity issues")
            : "Participant is facing connectivity issues",
          {
            body: "Participant has joined but cannot share microphone and/or camera",
            ttl: -1
          }
        );
        // either from dismiss action below or user action
        notif.onDismissed(() => {
          this.participantNotificationMap.delete(event.userEndpointId);
        });
        // keep it to be removed
        this.participantNotificationMap.set(event.userEndpointId, notif);
        // notify
        this.notification.notify(notif);
      }
    } else {
      if (this.participantNotificationMap.has(event.userEndpointId)) {
        this.notification.dismiss(
          this.participantNotificationMap.get(event.userEndpointId)
        );
      }
    }
  }

  private async initRoom() {
    try {
      let stream: MediaStream;

      if (this.isWhisper) {
        this.acceptConstraints();
      } else if (this.isAgent) {
        this.activityService.loading(!this.isDeviceHelpVisible());
        try {
          await this.conferenceService.createConference(
            this.conferenceId,
            this.interactionID
          );

          this.cdr.markForCheck();
          stream = await this.openStream();

          if (!stream || this.skipDeviceSetup) {
            this.acceptConstraints();
          }
        } catch (error) {
          if (error.response?.status === 404) {
            // conference doesn't exist
            this.handleCallEnded(TerminateReasonEnum.error, error.response);
          } else {
            this.notification.error(error.message || error, { ttl: 3000 });
            this.activityService.loading(false);
            this.leaveConference();
          }
        }
      } else {
        // show local stream first and then join conference
        stream = await this.openStream();

        if (this.isChildWindow) {
          // listen to cobrowse view requests and propagate to widget
          this.cobrowseService.listen(
            (
              ticket: string,
              originator: IEndpoint,
              requestType: CobrowseRequestType
            ) => {
              this.windowEventService.sendMessage(
                new CobrowseViewRequestAction({
                  ticket,
                  originator,
                  requestType
                })
              );
            }
          );
        }

        if (!stream || this.skipDeviceSetup) {
          this.acceptConstraints();
        }
      }
    } catch (error) {
      this.activityService.hide();
      this.notification.error(
        "Could not connect. Please refresh your page and try again."
      );
      this.errorHandler.handleError(error);
      this.leaveConference();
    }
  }

  recStarted(info: IRecorderInfo) {
    this.recorderStarted.emit(info);
  }

  recStopped(info: IRecorderInfo) {
    this.recorderStopped.emit(info);
  }

  captureGeolocation() {
    if (
      this.isCustomer &&
      this.config.publicParam(PublicParam.SNAPSHOT_GEOLOCATION_ENABLED)
    ) {
      const exists = this.conferenceService.getConferenceMetadata(
        ConferenceMetadataKeyEnum.geolocation
      ) as GeolocationMetadata;
      if (
        exists &&
        (exists.coordinates ||
          exists.error === GeolocationError.POSITION_UNAVAILABLE)
      ) {
        return;
      }

      const cnotif = new ConfirmNotification(
        "Location requested",
        "Would you like to share your location?"
      );
      cnotif.onConfirmed(async () => {
        try {
          const coordinates = await this.deviceService.captureGeolocation();
          this.conferenceService.setConferenceMetadata(
            new GeolocationMetadata(this.conferenceService.myself, coordinates)
          );
        } catch (ex) {
          this.conferenceService.setConferenceMetadata(
            new GeolocationMetadata(
              this.conferenceService.myself,
              undefined,
              ex.message
            )
          );
        }
      });
      cnotif.onCanceled(() => {
        this.conferenceService.setConferenceMetadata(
          new GeolocationMetadata(
            this.conferenceService.myself,
            undefined,
            GeolocationError.PERMISSION_DENIED
          )
        );
      });
      this.notification.notify(cnotif);
    }
  }

  filesDropped(files: File[], participant: IEndpoint) {
    this.transferService.sendFileToParticipant(
      files[0],
      participant,
      this.interaction
    );
  }

  // ------ view accessors ------------------

  isTileActionOnTop(stream: StreamState) {
    return (
      (this.pointerService.isStarted &&
        this.pointerService.isTarget(
          stream?.originator.endpoint,
          stream?.type,
          stream?.correlationId
        )) ||
      stream.video.state === "muted"
    );
  }

  isFileTransferAvailable(stream: StreamState) {
    return (
      stream.originator.endpoint !== this.conferenceService.myself.endpoint &&
      stream.type !== StreamTypes.SCREEN &&
      this.interactionType !== ConversationTypeEnum.voiceCall &&
      ((this.isAgent &&
        this.config.agentParamEnabled(AgentParam.FILE_TRANSFER_ENABLED)) ||
        (this.isCustomer &&
          this.config.customerParamEnabled(
            CustomerParam.FILE_TRANSFER_ENABLED
          )))
    );
  }

  get isDeviceSetupAvailable() {
    return computed(() => {
      return (
        !this.isWhisper && this.isDeviceSetupOpen && !this.isDeviceHelpVisible()
      );
    });
  }

  get allowedFileTypes() {
    return this.config.publicParam(PublicParam.ALLOWED_FILE_TYPES) || [];
  }

  get skipDeviceSetup() {
    const devicesChecked = this.deviceService.isDeviceSetupSkipped(
      this.userService.getActiveUser(),
      this.config
    );
    return (
      devicesChecked ||
      this.isAudioCall ||
      (this.isCustomer && this.isKioskMode) ||
      this.isWhisper ||
      this.isPopup
    );
  }

  get containerCssClass() {
    return {
      "tile-container-screen-only": this.isScreenShareFullScreen,
      "tile-container-mobile-android": this.isMobileAndroid,
      "tile-container-mobile-ios": this.isMobileIOS,
      "tile-container-ready": !this.isDeviceSetupOpen,
      "tile-container-no-top-bar": !this.isTopBarVisible,
      "tile-container-no-media-controls": !this.isControlsVisible,
      "tile-container-captions": this.isCaptionsOn,
      "tile-container-mobile": this.isMobile,
      // the talkdesk header is not in the pip window
      "tile-container-talkdesk": this.isTalkdeskApp && !this.pipActive(),
      "tile-container-layout-spotlight":
        this.currentLayout === LayoutEnum.spotlight,
      "tile-container-layout-spotlight-maximise": this.spotlightTileMaximise,
      "tile-container-pip": this.pipActive(),
      hidden: this.isOnHold
    };
  }

  get isTalkdeskApp() {
    return this.applicationService.isTalkdeskApp;
  }

  get isRecorderVisible() {
    return this.config.featureEnabled(FEATURES.RECORDER);
  }

  get isPopup() {
    return this.interaction.getOrigin() === ConversationOriginEnum.POPUP;
  }

  get interactionType() {
    return this.interaction.getType();
  }

  get isKioskMode(): boolean {
    return this.interaction.getOriginMode() === OriginModeEnum.kiosk;
  }

  get canTerminate(): boolean {
    // better to never terminate the call. We had scenarios where customer was on device setup, agent refreshed the page and both joined different rooms
    return false;
    // callbacks remain open because if we close accidentally, we cannot send a new link
    // return this.interaction.getOrigin() !== ConversationOriginEnum.CALLBACK &&
    // if it is NOT a widget, we can terminate
    // return (
    //   this.interaction.getOrigin() !== ConversationOriginEnum.WIDGET ||
    //   // if it IS a widget, then we can terminate if the conversation type is audio or video.
    //   // if it is a chat then we keep it open
    //   (this.interaction.getOrigin() === ConversationOriginEnum.WIDGET &&
    //     (this.interaction.getType() === ConversationTypeEnum.videoCall ||
    //       this.interaction.getType() === ConversationTypeEnum.voiceCall))
    // );
  }

  get isWidget() {
    return (
      DeviceService.isIFrame &&
      !this.isAgent &&
      this.interaction.getOrigin() === ConversationOriginEnum.WIDGET
    );
  }

  get isMobile() {
    return DeviceService.isMobile;
  }

  get isMobileIOS() {
    return DeviceService.isMobile && DeviceService.isiOS;
  }

  get isMobileAndroid() {
    return DeviceService.isMobile && !DeviceService.isiOS;
  }

  get isScreenShareActive() {
    return !!this.screenStream;
  }

  get isScreenShareLocal() {
    return this.conferenceService.isScreenShareStreamLocal;
  }

  get isLeaveAvailable() {
    return this.mediaRules.isLeaveAvailable(this.interaction);
    /*&& !this.isPopup && !(this.isWidget && this.isCustomer) || this.isKioskMode*/
  }

  get isLaserVisible() {
    return this.config.featureEnabled(FEATURES.LASER_TOOL);
  }

  isLaserActive(stream: StreamState) {
    return (
      this.pointerService.isTarget(
        stream.originator.endpoint,
        stream.type,
        stream.correlationId
      ) &&
      this.pointerService.isRequester(this.conferenceService.myself.endpoint)
    );
  }

  get deviceErrorAriaLabel() {
    return this.notAllowedError()
      ? "access blocked"
      : this.notFoundError()
      ? "Devices not available"
      : this.streamError()
      ? "Your devices are not responding"
      : this.isWebViewError()
      ? "Limited access to media devices"
      : "Accessing your camera and microphone is taking longer than usual";
  }

  get isOnHold() {
    return this.callHoldState.isOnHold;
  }

  get isRatingRequiredCustomer() {
    return (
      this.config.customerParamEnabled(CustomerParam.RATING_REQUIRED) ||
      this.mediaRules.isSurveyAvailableForInteraction(this.interaction)
    );
  }

  get isRatingRequiredAgent() {
    return (
      !this.isGuestAgent &&
      !this.isWhisper &&
      (this.config.agentParamEnabled(AgentParam.RATING_REQUIRED) ||
        this.mediaRules.isSurveyAvailableForInteraction(this.interaction))
    );
  }

  get isAudioCall() {
    return this.interaction.getType() === ConversationTypeEnum.voiceCall;
  }

  get isControlsEnabled() {
    return (
      !this.isRating &&
      this.isDeviceSetup &&
      // !this.isDeviceHelpVisible &&
      !this.isReviewingSnapshots &&
      !this.isCallEnded
    );
  }

  get isStreamControlsEnabled() {
    return (
      !this.isRating &&
      this.isDeviceSetup &&
      !this.isReviewingSnapshots &&
      !this.isCallEnded
    );
  }

  get isControlsVisible() {
    return (
      !this.isScreenShareFullScreen &&
      (this.mediaRules.isAudioAvailable ||
        this.mediaRules.isVideoAvailable ||
        this.mediaRules.isDisplayCaptureAvailable ||
        this.mediaRules.isLeaveAvailable(this.interaction) ||
        this.mediaRules.isHoldAvailable)
    );
  }

  get isTopBarVisible() {
    return (
      !this.isScreenShareFullScreen &&
      ((this.isCustomer &&
        this.config.customerParamEnabled(CustomerParam.TOP_BAR_ENABLED)) ||
        !this.isCustomer)
    );
  }

  // when in an audio call and the agent screen shares,
  // we only need to show the screen to the customer, not the participants
  // tried with layout.maximise but for a second or so the two participants are shown, before the screen stream arrives
  get isScreenShareFullScreen() {
    return (
      (this.isAudioCall && this.isWidget) ||
      (this.streams?.length === 1 && this.streams[0].type === "screen")
    );
  }

  get isSpotLightLayout() {
    return this.currentLayout === LayoutEnum.spotlight;
  }

  get isFloatingLayout() {
    return this.currentLayout === LayoutEnum.floating;
  }

  public pinTile(tile: ITile) {
    this.pinnedTile.set(tile);
    this.spotlightTile.set(tile);
    this.decideLayout();
  }

  public unpinTile() {
    const tile = this.pinnedTile();
    this.pinnedTile.set(null);
    this.spotlightTile.set(null);
    if (tile) {
      try {
        this.layout.tile(tile.container, tile.ratio);
      } catch (ex) {
        debugError(ex);
      }
    }
    this.decideLayout();
  }

  get isChildWindow() {
    return this.isWidget || this.isPopup;
  }

  get hasNoParticipants() {
    return (
      this.streams.length === 0 &&
      // !this.isDeviceHelpVisible &&
      !this.isDeviceSetupOpen &&
      this.isDeviceSetup &&
      !this.isLeavingByChoice &&
      !this.isRating &&
      !this.isSnapshotActive
    );
  }

  get isCaptionsEnabled() {
    return this.isCaptionsOn;
  }

  get isCaptionsAvailable() {
    return this.isCustomer && this.mediaRules.isCaptionsAvailable;
  }

  get isDeviceHelpVisible(): Signal<boolean> {
    return computed(() => {
      return (
        !this.isWhisper &&
        !this.isRating &&
        (this.notAllowedError() ||
          this.streamError() ||
          this.notFoundError() ||
          this.isWebViewError() ||
          this.isMediaRequestTimeoutError())
      );
    });
  }

  dismissHelp() {
    this.resetStreamErrors();
  }

  private resetStreamErrors() {
    this.notAllowedError.set(false);
    this.streamError.set(false);
    this.notFoundError.set(false);
    this.isWebViewError.set(false);
    this.isMediaRequestTimeoutError.set(false);
  }

  // ------ View actions ------------------

  acceptConstraints() {
    this.isDeviceSetupOpen = false;
    this.deviceService.isDevicesSetup = true;
    this.activityService.loading(true, "calling");
    this.transferService.getFilesOfInteractionFiltered(this.interactionID);

    // we need to set if the participant is in a popup, so that when he leaves, we can enable the 'send to chat'
    // on the agent sige
    this.conferenceService
      .joinConference(
        this.conferenceId,
        this.interaction.getOrigin(),
        this.interaction.getWhisperMode()
      )
      .then((terminatedObservable) => {
        this.activityService.hide();
        this.isDeviceSetup = true;

        if (
          this.isCustomer &&
          this.interaction.getChannel() === ConversationChannelEnum.aiAssistant
        ) {
          this.assistant.initLiveSession();
        }

        this.deviceSetupComplete.emit();

        this.callStarted.emit();

        if (this.isDeviceHelpVisible()) {
          this.conferenceService.updateEndpointMetadata({
            mediaDevicesError: true
          });
        }

        if (this.isChildWindow) {
          this.windowEventService.sendMessage(
            new CallStartedAction({
              audio: this.mediaRules.isAudioAvailable,
              video: this.mediaRules.isVideoAvailable,
              displayCapture: this.mediaRules.isDisplayCaptureAvailable
            })
          );
        }

        this.cdr.markForCheck();

        terminatedObservable.subscribe({
          next: (reason: TerminateReasonEnum) => {
            this.callEnding.emit({
              terminated: true,
              terminatedRemotely: true
            });
            this.handleCallEnded(reason);
            this.cdr.markForCheck();
          },
          error: (error) => {
            this.handleCallEnded(TerminateReasonEnum.error, error);
            this.cdr.markForCheck();
          }
        });
      })
      .catch((error) => {
        if (
          error.response?.status === 404 ||
          (error.response?.status === 403 &&
            error.response?.data.error === "participant limit reached")
        ) {
          // conference doesn't exist
          // or conference reached maximum participants
          this.handleCallEnded(
            TerminateReasonEnum.error,
            error.response?.data || error.data
          );
        } else {
          this.errorHandler.handleError(error);
          this.handleCallEnded(
            TerminateReasonEnum.error,
            error.response || error
          );
        }
        this.cdr.markForCheck();
      });
  }

  async handleCallEnded(reason: TerminateReasonEnum, error?: Error) {
    this.isCallEnded = true;
    // the snapshot review and rating below are shown in the tab
    this.pipStore.returnToTab();
    this.conferenceService.closeLocalStreams();
    this.tryTerminateCobrowse();
    if (this.mediaEffectsService.isMediaFilterOn) {
      this.mediaEffectsService.disable();
    }
    this.activityService.hide();
    try {
      if (!error) {
        // wait until we approve the snapshots
        if (this.isAgent && this.snapshotService.snapshotsTakenByMe) {
          await this.snapshotService.review();
          this.isReviewingSnapshots = false;
          this.isSnapshotActive = false;
          this.isSnapshotStarted = false;
        }
        // wait until we rate
        if (
          !this.skipRating &&
          ((this.isAgent && this.isRatingRequiredAgent) ||
            (!this.isAgent && this.isRatingRequiredCustomer))
        ) {
          this.isRating = true;
          this.callPendingRating.emit(true);
          if (this.isChildWindow) {
            this.windowEventService.sendMessage(new FeedbackRequestedAction());
          }
          await this.ratingService.showPopup(this.conferenceId);
        }
        this.skipRating = false;
      }
    } catch (ex) {
      // don't do anything, proceed as usual
      debugError(ex);
    }
    this.callPendingRating.emit(false);
    this.isRating = false;

    this.notification.dismissAll();

    // if is running inside widget
    if (this.isChildWindow) {
      this.windowEventService.sendMessage(
        new CallEndedAction({ reason, error })
      );
      // if the user opens a new call, show the device setup page again (Haris had this issue in Demos)
      this.deviceService.removeSkippedDeviceSetup();
    }
    this.isLeavingByChoice = true;

    // for some reason, ngOnDestroy is not called on the normal flow. Adding the delay fixes the issue
    // if however we wait for a rating, it is called :/
    await delay(1);
    this.callEnded.emit({ reason, error });
  }

  private async _openStreamWithFallbacks(
    permissions?: IMediaDevicesEvents["permissions"]
  ): Promise<MediaStream> {
    const handleEx = (ex) => {
      debugError("room: handleEx", ex);
      if (this.isAudioCall) {
        throw ex;
      } else {
        if (this.mediaRules.joinWithVideoOn) {
          return this.openVideoOnlyStream();
        } else {
          throw ex;
        }
      }
    };

    if (this.isAudioCall) {
      return this.openAudioOnlyStream();
    } else {
      if (permissions) {
        if (permissions.audioinput)
          return this.openAudioOnlyStream().catch(handleEx);
        if (permissions.videoinput)
          return this.openVideoOnlyStream().catch(handleEx);
      } else {
        // The phone-call case (stream resolves with muted tracks) is handled in
        // AudioInterruptionService.maybeKeepCameraLive(), which reacquires a
        // live video-only stream. This chain only matters when getUserMedia
        // actually rejects.
        return this.openDefaultStream()
          .catch((error) =>
            error.name === "TypeError" ? null : this.openAnyVideoStream()
          )
          .catch(() => this.openAudioOnlyStream())
          .catch(handleEx);
      }
    }
  }

  /**
   * Auto-start the secondary camera once per call. Guards against re-triggering
   * when the primary stream is republished (e.g. mic/cam toggle restarts it): the
   * `secondaryCameraAutoStarted` flag stays set even after the secondary is closed,
   * so an agent-closed secondary doesn't reopen on the next republish.
   */
  private get canAutoStartSecondaryCamera(): boolean {
    return (
      this.isCustomer &&
      this.config.customerParamEnabled(
        CustomerParam.AUTO_START_SECONDARY_CAMERA
      ) &&
      this.userService
        .getActiveUser()
        .hasCapability(UserCapabilityEnum.multiCam) &&
      this.config.customerParamEnabled(CustomerParam.CAMERA_ENABLED) &&
      this.config.customerParamEnabled(CustomerParam.CAMERA_DEFAULT_ON) &&
      !this.store.mySecondaryStream &&
      !this.secondaryCameraAutoStarted
    );
  }

  async tryOpenSecondaryCamera() {
    const secondaryCameras = MediaDevices.getDeviceList().filter(
      (d) =>
        d.kind === "videoinput" &&
        d.deviceId !== this.store.mystream.video?.deviceId
    );
    if (secondaryCameras.length > 0) {
      try {
        await this.local.openSecondaryCameraStream(
          secondaryCameras[0].deviceId
        );
        this.secondaryCameraAutoStarted = true;
      } catch (ex) {
        // race condition. agent may not have joined to get the event.
        // do we really need to notify? commented out for now.
        // this.conferenceService.getParticipantAgents().forEach((p) => {
        //   this.mediaRules.rejectRemoteStreamStart(
        //     { username: p.username, endpoint: p.endpoint },
        //     ex
        //   );
        // });
      }
    }
  }

  /**
   * will try to open a local stream. Once ready a 'localStreamReady$' observable will fire.
   */
  async openStream(permissions?: IMediaDevicesEvents["permissions"]) {
    this.resetStreamErrors();

    debug("room: isWebView", DeviceService.isWebView);

    let stream: MediaStream | null = this.local.pipe.input.isActive
      ? this.local.pipe.streamOut
      : await (!DeviceService.isWebView
          ? this._openStreamWithFallbacks(permissions)
          : withTimeout(
              new Promise<MediaStream>((resolve) => {
                // the timeout may run first but the user might accept after the 30 secs
                // in safari, this returns no stream so we need to handle it inside here
                this._openStreamWithFallbacks(permissions).then((s) => {
                  if (s) {
                    this.local.setMainStream(s);
                  }
                  return resolve(s);
                });
              }),
              30000 // wait for 30 seconds
            )
        )?.catch((error) => {
          this.isRequestingMedia.set(false);
          this.activityService.loading(false);

          this.mediaError(error);

          if (this.openStreamRetrySubscription) {
            this.openStreamRetrySubscription.unsubscribe();
            this.openStreamRetrySubscription = null;
          }

          this.openStreamRetrySubscription = this.local.permissions$
            .pipe(take(1))
            .subscribe((p) => {
              if (!this.mystream) {
                this.conferenceService.updateEndpointMetadata({
                  mediaDevicesError: false
                });
                // firefox specific, we get the permissions change when the user has accepted, not when the permissions changed
                if (!this.local.isUnmuting) this.openStream(p);
              }
            });

          return null;
        });

    this.activityService.loading(false);
    this.isRequestingMedia.set(false);

    if (stream) {
      this.local.setMainStream(stream);
    }

    return stream;
  }

  copyToClipboard(text: string) {
    copyTextToClipboard(text);
    this.notification.success("Copied to clipboard");
  }

  mediaError(error: Error) {
    if (error.message === "timeout") {
      this.isMediaRequestTimeoutError.set(true);
    } else {
      if (DeviceService.isWebView) {
        this.isWebViewError.set(true);
      } else {
        switch (error.name) {
          case "NotAllowedError":
            this.notAllowedError.set(true);
            break;
          case "NotFoundError":
            this.notFoundError.set(true);
            break;
          default:
            this.streamError.set(true);
            break;
        }
      }
    }
    setTimeout(() => {
      this.mediaErrorCardRef()?.nativeElement?.focus();
    }, 100);
  }

  private getFacingModeConstraints() {
    // do not get anything if not mobile
    if (!DeviceService.isMobile) return {};
    // Even if facingMode is not supported the default (user) camera will open
    if (this.mediaRules.defaultFacingMode === VideoFacingModeEnum.User)
      return {
        video: {
          facingMode: VideoFacingModeEnum.User
        }
      };

    const supportedConstraings =
      navigator.mediaDevices.getSupportedConstraints();
    if (supportedConstraings?.facingMode) {
      // facingMode is supported
      return {
        video: {
          facingMode: VideoFacingModeEnum.Environment
        }
      };
    } else {
      // try to find the rear camera device
      let rearCamera = MediaDevices.getDeviceList("videoinput").find(
        (device) =>
          device.label.toLowerCase().includes("back") ||
          device.label.toLowerCase().includes("rear") ||
          device.label.toLowerCase().includes("environment")
      );

      if (!rearCamera) {
        // just get the next camera that is not the current one
        rearCamera = MediaDevices.getDeviceList("videoinput").find(
          (device) => device.deviceId !== this.local.videoDevice?.deviceId
        );
      }
      if (rearCamera) {
        return {
          video: {
            deviceId: { exact: rearCamera.deviceId }
          }
        };
      } else {
        // we couldn't find any camera
        return {};
      }
    }
  }

  private async openDefaultStream(): Promise<MediaStream> {
    // Use saved preferred devices if available, otherwise fall back to active devices
    const audioDevice =
      MediaDevices.preferred?.audioinput ?? this.local.audioDevice;
    const videoDevice =
      MediaDevices.preferred?.videoinput ?? this.local.videoDevice;

    try {
      return this.local.openStream(
        false,
        {
          audio:
            this.mediaRules.isAudioAvailable && this.mediaRules.joinWithAudioOn,
          video:
            this.mediaRules.isVideoAvailable &&
            this.mediaRules.joinWithVideoOn &&
            !this.isAudioCall
        },
        MediaDevices.preferredConstraints,
        this.local.getDeviceConstraints(audioDevice, videoDevice),
        this.getFacingModeConstraints(),
        ...Object.values(MediaDevices.constraints)
      );
    } catch (error) {
      if (error.name !== "TypeError") {
        debug(
          `failed to access video device with constraints ${this.local.pipe.input.constraints.video} and error`,
          error
        );
        this.errorHandler.handleError(error);
      }

      throw error;
    }
  }

  private async openAnyVideoStream(): Promise<MediaStream> {
    try {
      return this.local.openStream(
        false,
        {
          audio: true,
          video: !this.isAudioCall
        },
        this.local.getDeviceConstraints(
          this.local.audioDevice,
          this.local.videoDevice
        )
      );
    } catch (error) {
      this.notification.error(this.translate.instant(error.message || error), {
        ttl: 3000
      });
      throw error;
    }
  }

  private async openAudioOnlyStream(): Promise<MediaStream> {
    try {
      return this.local.openStream(false, {
        audio: true,
        video: false
      });
    } catch (error) {
      this.notification.error(error.message || error, { ttl: 3000 });
      throw error;
    }
  }

  private async openVideoOnlyStream(): Promise<MediaStream> {
    try {
      return this.local.openStream(false, {
        audio: false,
        video: true
      });
    } catch (error) {
      this.notification.error(error.message || error, { ttl: 3000 });
      throw error;
    }
  }

  public exitConference(): void {
    this.tryTerminateCobrowse();
    this.callEnding.emit({ terminated: false, terminatedRemotely: false });
    this.conferenceService.leaveSession(TerminateReasonEnum.leave);
  }

  public terminateConference(): void {
    this.tryTerminateCobrowse();
    this.callEnding.emit({ terminated: true, terminatedRemotely: false });
    this.conferenceService.terminateSession();
  }

  public leaveConference(reason?: TerminateReasonEnum): void {
    this.isLeavingByChoice = true;
    // no session yet, just exit
    if (!this.conferenceService.sessionExists) {
      this.conferenceService.closeLocalStreams();
      return this.exit.emit();
    }
    let terminated = false;
    this.tryTerminateCobrowse();
    try {
      switch (reason) {
        // unpublish is performed by server
        case TerminateReasonEnum.terminate:
          this.conferenceService.terminateSession(reason);
          terminated = true;
          break;
        case TerminateReasonEnum.conversationEnded:
          if (this.isCustomer) {
            this.conferenceService.leaveSession(
              reason || TerminateReasonEnum.leave
            );
          } else {
            this.conferenceService.terminateSession(reason);
            terminated = true;
          }
          break;
        default:
          this.conferenceService.leaveSession(
            reason || TerminateReasonEnum.leave
          );
          break;
      }
    } catch (ex) {
      debug(ex);
    }
    this.callEnding.emit({ terminated, terminatedRemotely: false });
  }

  cancel(): void {
    this.isLeavingByChoice = true;
    this.conferenceService.closeLocalStreams();
    if (this.mediaEffectsService.isMediaFilterOn) {
      this.mediaEffectsService.disable();
    }
    this.exit.emit();
  }

  reload() {
    window.location.reload();
  }

  private tryTerminateCobrowse() {
    if (
      this.isAgent &&
      (this.cobrowseService.isStarted || this.cobrowseService.isPendingApproval)
    ) {
      this.cobrowseService.terminate();
    }
  }

  // ------- track *ngFor functions -----------

  // if we track by mediaStream.id then we remove/add new tiles on mute/unmute :/
  trackByMediaIdFn(index: number, stream: StreamState) {
    return `${stream.type}_${stream.primary}_${stream.originator.endpoint}`;
  }

  // ------ view helpers ----------------------

  get isParticipantPresent() {
    return this.endpoints.length > 0;
  }

  get isAgentPresent() {
    return (
      this.endpoints.filter((e) =>
        (e.metadata as IEndpointMetadata)?.roles?.includes(UserRoleEnum.agent)
      ).length > 0
    );
  }

  get isCustomerPresent() {
    return (
      this.endpoints.filter((e) =>
        (e.metadata as IEndpointMetadata)?.roles?.includes(
          UserRoleEnum.customer
        )
      ).length > 0
    );
  }

  get endpoint() {
    return this.store.endpoint;
  }
}
